"use client";

import ContactList from "./ContactList";
import { useState, useRef } from "react";
import {
  Box,
  Divider,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import {
  Tabs,
  TabList,
  Tab,
  SplitButton,
  ListFilterBar,
  ResultCount,
} from "@/ui";
import { BuildingIcon, TagIcon, UserIcon } from "lucide-react";
import { CSVBoxButton } from "@csvbox/react";
import toast from "react-hot-toast";
import { useRouter } from "next/navigation";
import config from "@lib/config";
import useRecruiterDetails from "@lib/user/useRecruiterDetails";
import ContactDetailsDialog from "./parts/ContactDetailsDialog";
import PageTitle from "@components/shared/Typography/PageTitle";
import Paging from "@components/shared/table/Paging";
import useSearch from "@lib/shared/useSearch";
import useUrlListState, { useUrlSync } from "@lib/shared/useUrlListState";
import { CONTACT_SORT_OPTIONS, TAG_TYPE } from "@lib/enums";
import useContacts from "@lib/contacts/useContacts";
import useContactNames from "@lib/contacts/useContactNames";
import useContactCompanies from "@lib/contacts/useContactCompanies";
import useTags from "@lib/tags/useTags";
import AddContactNoteDialog from "./parts/AddContactNoteDialog";

const Container = styled(Box)(({ theme }) => ({
  padding: "40px 46px 40px 46px",

  [theme.breakpoints.down("md")]: {
    padding: "30px 20px",
  },
}));

export default function Contacts() {
  const router = useRouter();
  const { recruiter } = useRecruiterDetails();
  const hasTeamSharing = recruiter?.user?.has_team_sharing ?? false;

  const { initial, updateUrl } = useUrlListState({
    defaultSortProperty: CONTACT_SORT_OPTIONS.UPDATED_AT,
    defaultSortDirection: "desc",
  });

  const [page, setPage] = useState(initial.page);
  const [activeFilters, setActiveFilters] = useState(initial.filters);
  const [searchFilters, setSearchFilters] = useState({});
  const [sortBy, setSortBy] = useState(initial.sortBy);

  const { names } = useContactNames();
  const { companies } = useContactCompanies();

  const handleSuggestionSelect = (value) => {
    if (companies.includes(value)) {
      setSearchFilters({ company: value });
      setPage(1);
      return true;
    }
    // Partial company match — pass raw text; API does LIKE '%value%' for single values
    const hasPartialMatch = companies.some(c => c.toLowerCase().includes(value.toLowerCase()));
    if (hasPartialMatch) {
      setSearchFilters({ company: value });
      setPage(1);
      return true;
    }
    return false;
  };

  const { SearchField, search, searchInput } = useSearch(setPage, {
    placeholder: "Search contacts...",
    helperText: "Search by name or company",
    submitButton: true,
    suggestions: [...names, ...companies],
    onSuggestionSelect: handleSuggestionSelect,
    initialValue: initial.search,
  });

  useUrlSync(updateUrl, { page, sortBy, search, filters: activeFilters });

  // Clear the company filter when the search box is emptied. Handled during
  // render (React's "You Might Not Need an Effect") rather than in an effect.
  const [prevSearchInput, setPrevSearchInput] = useState(searchInput);
  if (searchInput !== prevSearchInput) {
    setPrevSearchInput(searchInput);
    if (!searchInput) setSearchFilters({});
  }

  const { tags } = useTags({
    page: 1,
    sortBy: { property: "name", direction: "asc" },
    search: "",
    filters: { with_default: true, unique: "slug", type: TAG_TYPE.CONTACT },
  });

  const filterFields = [
    {
      key: "name",
      label: "Name",
      type: "multiselect",
      icon: <UserIcon size={14} />,
      searchable: true,
      options: names.map((n) => ({ value: n, label: n })),
    },
    {
      key: "company",
      label: "Company",
      type: "multiselect",
      icon: <BuildingIcon size={14} />,
      searchable: true,
      options: companies.map((c) => ({ value: c, label: c })),
    },
    {
      key: "tags",
      label: "Tags",
      type: "multiselect",
      icon: <TagIcon size={14} />,
      options: (tags ?? []).map((tag) => ({ value: tag.name, label: tag.name })),
    },
  ];

  const getFilterValues = (field) =>
    activeFilters.find((f) => f.field === field)?.values ?? [];

  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
    setPage(1);
  };

  const filteredTagNames = activeFilters
    .filter((f) => f.field === "tags")
    .flatMap((f) => f.values);

  const filteredNames = getFilterValues("name");
  const filteredCompanies = getFilterValues("company");
  const allCompanies = [...filteredCompanies, ...(searchFilters.company ? [searchFilters.company] : [])];

  const activeApiFilters = {
    source: "floats",
    ...(filteredTagNames.length > 0 ? { tags: filteredTagNames } : {}),
    ...(filteredNames.length > 0 ? { name: filteredNames.join("|") } : {}),
    ...(allCompanies.length > 0 ? { company: allCompanies.join("|") } : {}),
  };

  // My Contacts only
  const { contacts, lastPage, total, from, to, hasMore, contactsIsLoading, invalidate } =
    useContacts({
      page,
      sortBy,
      search,
      filters: activeApiFilters,
    });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [contact, setContact] = useState(null);
  const csvLaunchRef = useRef(null);

  const csvUser = { user_id: recruiter?.id, email: recruiter?.user?.email };

  const handleCsvImport = (result, data) => {
    if (result) {
      toast.success(`Importing ${data.row_success} contacts! The new contacts should be visible within a few minutes.`, { duration: 8000 });
      refresh();
    } else {
      toast.error("Import failed. Please try again.");
    }
  };

  const splitItems = [
    { label: "Add manually", onClick: () => openAddDialog() },
    { label: "Import CSV", onClick: () => csvLaunchRef.current?.() },
  ];

  const contactsIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <circle cx="8" cy="7" r="3.5" />
      <path d="M2 21v-1a6 6 0 0112 0v1" />
      <path d="M16 3.13a4 4 0 010 7.75" />
      <path d="M22 21v-1a4 4 0 00-3-3.87" />
    </svg>
  );

  const openAddNote = (contact) => {
    setContact(contact);
    setAddNoteOpen(true);
  };

  const openAddDialog = () => {
    setContact(null);
    setDetailsOpen(true);
  };

  const openEditDialog = (contact) => {
    setContact(contact);
    setDetailsOpen(true);
  };

  const refresh = () => {
    setTimeout(() => {
      invalidate();
    }, 3000);
  };

  const handleTabChange = (value) => {
    if (value === "team") router.push("/team/contacts");
  };

  return (
    <Box>
      <Container>
          <Stack
            sx={{ mb: 2 }}
            direction="row"
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <PageTitle>Contacts</PageTitle>
            <Box display={"flex"} columnGap={"4px"} alignItems="center">
              <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
                <CSVBoxButton
                  licenseKey={config.csvboxContactSheetLicenceKey}
                  user={csvUser}
                  onImport={handleCsvImport}
                  render={(launch) => { csvLaunchRef.current = launch; return <span />; }}
                >
                  csv
                </CSVBoxButton>
              </div>
              <SplitButton
                label="Add contacts"
                startIcon={contactsIcon}
                size="md"
                variant="accent"
                items={splitItems}
                mainOpensMenu
              />
            </Box>
          </Stack>
          <Box sx={{ mb: 6, width: "60%" }}>
            <Typography variant="body2">
              Your contacts are the people within the companies that you work
              with. You can add new contacts manually or import them from a
              spreadsheet.
            </Typography>
          </Box>
          {hasTeamSharing ? (
            <Tabs value="mine" onChange={handleTabChange}>
              <TabList>
                <Tab value="mine">My contacts</Tab>
                <Tab value="team">Team contacts</Tab>
              </TabList>
            </Tabs>
          ) : (
            <Divider />
          )}
          <Box sx={{ my: 4 }}>
            <Box sx={{ width: '50%', mb: 2 }}>{SearchField}</Box>
            <ListFilterBar
              fields={filterFields}
              filters={activeFilters}
              onChange={handleFiltersChange}
            />
          </Box>
          {!contactsIsLoading && contacts?.length > 0 && (
            <ResultCount
              from={from}
              to={to}
              total={total}
              noun="contact"
            />
          )}
          <ContactList
            contacts={contacts}
            sortBy={sortBy}
            setSortBy={setSortBy}
            openAddNote={openAddNote}
            openEditDialog={openEditDialog}
            isLoading={contactsIsLoading}
          />
          <Paging
            page={page}
            lastPage={lastPage}
            setPage={setPage}
            hasMore={hasMore}
          />
          <ContactDetailsDialog
            contactToEdit={contact}
            open={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            recruiterId={recruiter?.id}
          />
          <AddContactNoteDialog
            open={addNoteOpen}
            onClose={() => setAddNoteOpen(false)}
          />
        </Container>
    </Box>
  );
}
